import React, { useState, useEffect, FC } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthUser, useIsAuthenticated } from "react-auth-kit";
import { KaerIcon } from "@/icons/kaerIcon";
import RoundedContentBox from "@/components/general/roundedContentBox";
import { BeatLoader } from "react-spinners";
import {
  BOOKED_APPOINTMENTS_SECTION_ID,
  APPOINTMENT_REQUESTS_SECTION_ID,
  APPOINTMENT_REQUESTS_COMPANY_SECTION_ID,
} from "@/components/appointmentRequest/appointmentRequestOpen";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import {
  Box,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import "@/routes/startPage.css";
import { api } from "@/helper/api";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import AppointmentRequestSampleIcon from "@/icons/appointmentRequestSampleIcon";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import ManagerAppointmentRequestListItem from "@/components/appointmentRequest/managerAppointmentRequestListItem";
import appointmentOpenStyles from "@/components/appointmentRequest/appointmentRequestOpen.module.css";
import {
  getLmsCoursesRoute,
  getLmsEnrolmentsRoute,
  shouldBlockLmsForSubdomain,
  type LmsMode,
} from "@/components/lms/lms.utils";
import { useManagerLmsAccess } from "@/context/managerLmsAccessContext";
import IntroVideoCard from "@/components/introVideo/introVideoCard";
import DraftCompaniesWidget from "@/components/company/draftCompaniesWidget";

export default function StartPage() {
  const [todoCount, setTodoCount]: any = useState();
  const [openEnrolmentCount, setOpenEnrolmentCount] = useState<number>();
  const [appointmentRequestCount, setAppointmentRequestCount]: any = useState();
  const [workerCount, setWorkerCount] = useState<number>();
  const [departmentCount, setDepartmentCount] = useState<number>();
  const [newFilesCount, setNewFilesCount] = useState<number>();
  const [registeredWorkerCount, setRegisteredWorkerCount] = useState<number>();
  const [lmsEnrolmentCount, setLmsEnrolmentCount] = useState<number>();
  const [lmsCompletedEnrolmentCount, setLmsCompletedEnrolmentCount] =
    useState<number>();
  const [completedExaminations, setCompletedExaminations] = useState<any>([]);
  const [upComingAppointmentRequests, setUpcomingAppointmentRequests] =
    useState<any>([]);
  const [upComingAppointments, setUpcomingAppointments] = useState<any>([]);
  const [introVideoUrl, setIntroVideoUrl] = useState<string | null>(null);
  const { handleError, errorModal } = useErrorModal();

  const { t } = useTranslation();
  const [firstName, setFirstName] = useState<string | undefined>("");

  const getAuth = useAuthUser();
  const isAuthenticated = useIsAuthenticated();
  const selectedSubject = useSelector((state: RootState) => state.parent);
  const children = useSelector((state: RootState) => state.children);
  const loginPermissions = useSelector(
    (state: RootState) => state.login?.permissions ?? []
  );
  const managerLmsAccess = useManagerLmsAccess();
  useEffect(() => {
    if (isAuthenticated()) {
      const auth = getAuth();

      if (auth && auth.email) {
        setFirstName(auth.first_name);
      }
    }
  }, [getAuth, isAuthenticated]);

  const appointmentLocation = (item: any) => {
    const building = item?.building_information;
    if (!building) {
      return "";
    }

    const { name, street_house, zip, city } = building;
    const locationParts = [];

    if (name) locationParts.push(name);
    if (street_house) locationParts.push(street_house);

    if (zip || city) {
      const combined = [zip, city].filter((part) => part != null).join(" ");
      locationParts.push(combined);
    }

    return locationParts.join(", ");
  };

  /** Booked / upcoming appointment row — matches Termine layout (no action buttons). */
  const renderBookedUpcomingAppointmentRow = (item: any, rowIndex: number) => {
    const rowKey = item.id ?? `booked-${rowIndex}`;
    const appointmentDate = item.datetime
      ? new Date(item.datetime).toLocaleString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "";
    const appointmentTime = item.datetime
      ? new Date(item.datetime).toLocaleString("de-DE", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

    const examStrings: string[] = Array.isArray(item.examinations)
      ? item.examinations.filter(
          (s: unknown) => typeof s === "string" && s.trim() !== ""
        )
      : [];
    const examObjectList = Array.isArray(item.examination_set)
      ? item.examination_set
      : [];

    const dateTimePrimary =
      appointmentDate || appointmentTime ? (
        <Typography
          className={appointmentOpenStyles.displayInline}
          component="span"
          variant="body2"
          color="text.primary"
          sx={{ fontWeight: "bold" }}
        >
          {appointmentDate && appointmentTime
            ? `${appointmentDate} ${t("at_time", { time: appointmentTime })}`
            : appointmentDate || appointmentTime || ""}
        </Typography>
      ) : null;

    const examinationPrimaryContent = (
      <>
        {examObjectList.map((examination: any) => (
          <ListItemText
            secondaryTypographyProps={{ component: "div" }}
            key={
              examination.id != null
                ? String(examination.id) + (examination.name ?? "")
                : `exam-${examination.name}-${examination.description}`
            }
            primary={
              <Typography
                className={appointmentOpenStyles.displayBlock}
                component="span"
                variant="body2"
                color="text.primary"
              >
                {examination.examination_name ?? examination.name}
              </Typography>
            }
            secondary={
              <Typography
                className={appointmentOpenStyles.displayBlock}
                sx={{ whiteSpace: "preserve" }}
                component="span"
                variant="body2"
                color="text.secondary"
              >
                {examination?.description}
              </Typography>
            }
          />
        ))}
        {examObjectList.length === 0 &&
          examStrings.map((line, idx) => (
            <ListItemText
              key={`dash-exam-${idx}-${line.slice(0, 40)}`}
              primary={
                <Typography
                  className={appointmentOpenStyles.displayBlock}
                  component="span"
                  variant="body2"
                  color="text.primary"
                >
                  {line}
                </Typography>
              }
            />
          ))}
        {examObjectList.length === 0 &&
          examStrings.length === 0 &&
          item.category_examinations && (
            <ListItemText
              primary={
                <Typography
                  className={appointmentOpenStyles.displayBlock}
                  component="span"
                  variant="body2"
                  color="text.primary"
                >
                  {item.category_examinations}
                </Typography>
              }
            />
          )}
      </>
    );

    const hasExaminationContent =
      examObjectList.length > 0 ||
      examStrings.length > 0 ||
      Boolean(item.category_examinations);

    return (
      <React.Fragment key={rowKey}>
        <ListItem
          className={`${appointmentOpenStyles.listItem} dashboardUpcomingAppointmentRow`}
          sx={{
            "&& .MuiListItemSecondaryAction-root": {
              paddingLeft: "2rem",
            },
          }}
        >
          <ListItemIcon className={appointmentOpenStyles.listItemIcon}>
            <AppointmentRequestSampleIcon
              width="2.5rem"
              height="2.5rem"
              withBackground={true}
            />
          </ListItemIcon>
          <ListItemText
            secondaryTypographyProps={{ component: "div" }}
            primary={
              hasExaminationContent ? examinationPrimaryContent : "\u00a0"
            }
            secondary={
              <>
                {dateTimePrimary}
                {appointmentLocation(item) && (
                  <ListItemText primary={appointmentLocation(item)} />
                )}
                {item.building_information?.customer_information && (
                  <ListItemText
                    primary={
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        {t("customer_information")}:{" "}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        {item.building_information?.customer_information}
                      </Typography>
                    }
                  />
                )}
              </>
            }
          />
        </ListItem>
      </React.Fragment>
    );
  };

  useEffect(() => {
    api.genericApiRequest({
      entity: "dashboard",
      method: "get",
      successHandler: (res: any) => {
        if (res.data && res.data.completed_examinations !== undefined) {
          setCompletedExaminations(res.data.completed_examinations);
        }
        if (res.data && res.data.worker_count) {
          setWorkerCount(res.data.worker_count);
        } else {
          setWorkerCount(0);
        }
        if (res.data && res.data.department_count) {
          setDepartmentCount(res.data.department_count);
        } else {
          setDepartmentCount(0);
        }
        if (res.data && res.data.new_files) {
          setNewFilesCount(res.data.new_files);
        } else {
          setNewFilesCount(0);
        }
        if (res.data && res.data.registered_worker_count) {
          setRegisteredWorkerCount(res.data.registered_worker_count);
        } else {
          setRegisteredWorkerCount(0);
        }
        if (res.data && res.data.lms_enrolment_count !== undefined) {
          setLmsEnrolmentCount(res.data.lms_enrolment_count);
        } else {
          setLmsEnrolmentCount(0);
        }
        if (res.data && res.data.lms_completed_enrolment_count !== undefined) {
          setLmsCompletedEnrolmentCount(res.data.lms_completed_enrolment_count);
        } else {
          setLmsCompletedEnrolmentCount(0);
        }
        if (res.data && res.data.todo_count) {
          setTodoCount(res.data.todo_count);
        } else {
          setTodoCount(0);
        }
        if (res.data && res.data.open_enrolment_count !== undefined) {
          setOpenEnrolmentCount(res.data.open_enrolment_count);
        } else {
          setOpenEnrolmentCount(0);
        }
        if (res.data && res.data.appointment_request_count) {
          setAppointmentRequestCount(res.data.appointment_request_count);
        } else {
          setAppointmentRequestCount(0);
        }
        if (res.data && res.data.upcoming_appointment_requests !== undefined) {
          setUpcomingAppointmentRequests(
            res.data.upcoming_appointment_requests
          );
        }
        if (res.data && res.data.upcoming_appointments !== undefined) {
          setUpcomingAppointments(res.data.upcoming_appointments);
        }
        setIntroVideoUrl(res.data?.intro_video_url ?? null);
      },
      failHandler: (error: any) => {
        setTodoCount(0);
        setOpenEnrolmentCount(0);
        setAppointmentRequestCount(0);
        setWorkerCount(0);
        setRegisteredWorkerCount(0);
        setLmsEnrolmentCount(0);
        setLmsCompletedEnrolmentCount(0);
        setDepartmentCount(0);
        setNewFilesCount(0);
        setIntroVideoUrl(null);
        handleError(error);
      },
    });
  }, [selectedSubject.subjectId, children]);

  let todoCounterContentBox = <BeatLoader />;
  let openEnrolmentCounterContentBox = <BeatLoader />;
  let appointmentRequestCounterContentBox = <BeatLoader />;
  let registeredWorkerCountContentBox = <BeatLoader />;
  let enrolmentProgressContentBox = <BeatLoader />;
  let statisticContentBox = <BeatLoader />;
  let contactContentBox = <BeatLoader />;
  let completedExaminationContentBox = <BeatLoader />;
  let upcomingApointmentRequestsContentBox = <BeatLoader />;

  //statisticList
  let workerCountContentBox = <></>;
  let departmentCountContentBox = <></>;
  let newFilesCountContentBox = <></>;

  let toDoString = t("todo_single");
  if (todoCount !== undefined && todoCount > 1) {
    toDoString = t("todo_multi");
  }

  let appointmentRequestString = t("appointmentrequest_single");
  if (appointmentRequestCount !== undefined && appointmentRequestCount > 1) {
    appointmentRequestString = t("appointmentrequest_multi");
  }

  if (todoCount !== undefined) {
    todoCounterContentBox = (
      <section className="contentSection">
        <span className="dashboardStatIcon" aria-hidden>
          <KaerIcon
            width="3.25rem"
            height="3.25rem"
            color="#8C1EC8"
            icon="todo"
          />
        </span>
        <h2 className="sectionHeading">
          {todoCount === 0 ? t("todo_none_open") : `${todoCount} ${toDoString}`}
        </h2>
      </section>
    );
  }

  const openEnrolmentString =
    openEnrolmentCount === 1 ? t("course") : t("courses");

  if (openEnrolmentCount !== undefined) {
    openEnrolmentCounterContentBox = (
      <section className="contentSection">
        <span className="dashboardStatIcon" aria-hidden>
          <KaerIcon
            width="3.25rem"
            height="3.25rem"
            color="#194E7B"
            icon="courses"
          />
        </span>
        <h2 className="sectionHeading">
          {openEnrolmentCount === 0
            ? t("tuition_todo_none_open")
            : `${openEnrolmentCount} ${openEnrolmentString}`}
        </h2>
      </section>
    );
  }
  if (appointmentRequestCount !== undefined) {
    appointmentRequestCounterContentBox = (
      <section className="contentSection">
        <span className="dashboardStatIcon" aria-hidden>
          <KaerIcon
            width="3.25rem"
            height="3.25rem"
            color="#FF6428"
            icon="newAppointment"
          />
        </span>
        <h2 className="sectionHeading">
          {appointmentRequestCount === 0
            ? t("appointmentrequest_none_open")
            : `${appointmentRequestCount} ${appointmentRequestString}`}
        </h2>
      </section>
    );
  }

  let appointmentRequestsBox = <></>;
  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];
  const hasLmsAccess =
    !shouldBlockLmsForSubdomain({
      subdomain,
      permissions: loginPermissions,
    }) &&
    (!["manage", "portal"].includes(subdomain) || managerLmsAccess.canAccess);
  const lmsMode: LmsMode =
    subdomain === "admin"
      ? "administrator"
      : subdomain === "manage"
      ? "manager"
      : "learner";
  const showTuitionTodoCard =
    hasLmsAccess && subdomain !== "manage" && subdomain !== "admin";
  const topStatColumnMd = showTuitionTodoCard ? 4 : 6;

  if (subdomain != "admin") {
    appointmentRequestsBox = (
      <NavLink
        className="dashboardNavlink"
        to={`/appointmentrequestopen#${APPOINTMENT_REQUESTS_SECTION_ID}`}
      >
        <RoundedContentBox className="dashboardTopStatCard dashboardClickableSurface">
          {appointmentRequestCounterContentBox}
        </RoundedContentBox>
      </NavLink>
    );
  }

  /* Dashboard may return slim rows without examination_set; still show them under Gebuchte Termine */
  const manageBookedAppointmentsList =
    subdomain === "manage" ? upComingAppointments : [];

  let manageDepartmentAppointmentRequestItems = <></>;
  let manageBookedAppointmentItems = <></>;
  let portalUpcomingAppointmentListItems = <></>;

  if (subdomain === "manage" && upComingAppointmentRequests.length !== 0) {
    manageDepartmentAppointmentRequestItems = upComingAppointmentRequests.map(
      (item: any) => (
        <ManagerAppointmentRequestListItem
          key={item.id ?? `${item.title}-${item.start_day}`}
          item={item}
        />
      )
    );
  }

  if (subdomain === "manage" && manageBookedAppointmentsList.length > 0) {
    manageBookedAppointmentItems = manageBookedAppointmentsList.map(
      (item: any, bookedIndex: number) =>
        renderBookedUpcomingAppointmentRow(item, bookedIndex)
    );
  }

  if (subdomain === "portal" && upComingAppointments.length !== 0) {
    portalUpcomingAppointmentListItems = upComingAppointments.map(
      (item: any, index: number) =>
        renderBookedUpcomingAppointmentRow(item, index)
    );
  }

  let manageBookedAppointmentsCardInner = <></>;
  let manageDepartmentRequestsCardInner = <></>;

  if (subdomain === "manage") {
    manageBookedAppointmentsCardInner = (
      <div className="dashboardUpcomingCardInner">
        <h2
          className="secondHeading noMargin"
          id="manageBookedAppointmentsHeading"
        >
          {t("upcoming_appointments_company")}
        </h2>
        <List
          className="fullWidth noPadding"
          aria-labelledby="manageBookedAppointmentsHeading"
        >
          {manageBookedAppointmentsList.length === 0 ? (
            <ListItem>
              <Typography variant="body2" color="text.secondary">
                {t("no_upcoming_appointments")}
              </Typography>
            </ListItem>
          ) : (
            manageBookedAppointmentItems
          )}
        </List>
      </div>
    );
    manageDepartmentRequestsCardInner = (
      <>
        <NavLink
          className="dashboardNavlinkList"
          to={`/appointmentrequestopen#${APPOINTMENT_REQUESTS_COMPANY_SECTION_ID}`}
        >
          <h2
            className="secondHeading noMargin"
            id="manageDepartmentRequestsHeading"
          >
            {t("upcoming_appointments_customer")}
          </h2>
        </NavLink>
        <List
          className="fullWidth noPadding"
          aria-labelledby="manageDepartmentRequestsHeading"
        >
          {upComingAppointmentRequests.length === 0 ? (
            <ListItem>
              <Typography variant="body2" color="text.secondary">
                {t("no_upcoming_appointments")}
              </Typography>
            </ListItem>
          ) : (
            manageDepartmentAppointmentRequestItems
          )}
        </List>
      </>
    );
  } else {
    upcomingApointmentRequestsContentBox = (
      <div className="dashboardUpcomingCardInner">
        <h2 className="secondHeading">{t("upcoming_appointments")}</h2>
        {upComingAppointments.length > 0 ? (
          <List
            className="fullWidth noPadding"
            aria-labelledby="portalUpcomingAppointmentsList"
          >
            {portalUpcomingAppointmentListItems}
          </List>
        ) : (
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            {t("no_upcoming_appointments")}
          </Typography>
        )}
      </div>
    );
  }

  if (completedExaminations.length !== 0) {
    completedExaminationContentBox = (
      <section className="completedExaminationWrapper">
        <div className="completedExaminationsStringWrapper">
          <h2 className="secondHeading ">{t("completed_examinations")}</h2>
          <span className="completedExaminationsSpan bold">
            {completedExaminations.reduce(
              (total: any, item: any) => total + item.count,
              0
            )}
          </span>
        </div>
        <List className="fullWidth">
          {completedExaminations.map((item: any, index: number) => {
            const examinationLabel = item.examination_name ?? item.name ?? "";
            return (
              <React.Fragment
                key={
                  examinationLabel !== ""
                    ? `${examinationLabel}-${index}`
                    : `completed-examination-${index}`
                }
              >
                <ListItem
                  className="countListItem"
                  secondaryAction={item.count.toString()}
                >
                  <ListItemText
                    primary={examinationLabel}
                    primaryTypographyProps={{
                      paddingRight: 8,
                    }}
                    className="countListItemText"
                  />
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </section>
    );
  }

  if (newFilesCount !== undefined) {
    newFilesCountContentBox = (
      <ListItem
        className="countListItem"
        secondaryAction={newFilesCount.toString()}
      >
        <ListItemText
          primary={t("new_files")}
          primaryTypographyProps={{
            paddingRight: 8,
          }}
          className="countListItemText"
        />
      </ListItem>
    );
  }

  if (departmentCount !== undefined) {
    departmentCountContentBox = (
      <ListItem
        className="countListItem"
        secondaryAction={departmentCount.toString()}
      >
        <ListItemText
          primary={t("departments")}
          primaryTypographyProps={{
            paddingRight: 8,
          }}
          className="countListItemText"
        />
      </ListItem>
    );
  }

  if (workerCount !== undefined && registeredWorkerCount !== undefined) {
    workerCountContentBox = (
      <ListItem
        className="countListItem"
        secondaryAction={workerCount.toString()}
      >
        <ListItemText
          primary={t("customer")}
          primaryTypographyProps={{
            paddingRight: 8,
          }}
          className="countListItemText"
        />
      </ListItem>
    );
  }

  const getDashboardProgressValue = (
    completedCount: number,
    totalCount: number
  ) => (totalCount > 0 ? Math.round((100 / totalCount) * completedCount) : 0);

  const renderDashboardProgressSection = (
    title: string,
    label: string,
    completedCount: number,
    totalCount: number
  ) => (
    <section className="dashboardProgressSection">
      <h2 className="secondHeading">{title}</h2>
      <LinearProgress
        value={getDashboardProgressValue(completedCount, totalCount)}
        variant="determinate"
        className="registeredWorkersProgressBar"
        classes={{ bar: "linearProgressBar" }}
      />
      <Typography
        variant="body2"
        pt={2}
        className="registeredWorkersText"
      >{`${label}: ${completedCount} / ${totalCount}`}</Typography>
    </section>
  );

  if (workerCount !== undefined && registeredWorkerCount !== undefined) {
    registeredWorkerCountContentBox = renderDashboardProgressSection(
      t("customer"),
      t("registered"),
      registeredWorkerCount,
      workerCount
    );
  }

  if (
    lmsEnrolmentCount !== undefined &&
    lmsCompletedEnrolmentCount !== undefined
  ) {
    enrolmentProgressContentBox = renderDashboardProgressSection(
      t("course_enrolments"),
      t("completed"),
      lmsCompletedEnrolmentCount,
      lmsEnrolmentCount
    );
  }

  if (subdomain === "manage") {
    statisticContentBox = (
      <>
        <h2 className="secondHeading statisticsSecondHeading">
          {t("statistics")}
        </h2>
        <List className="dashboardStatisticList">
          <NavLink className="dashboardNavlinkList" to="/mitarbeiter">
            {departmentCountContentBox}
          </NavLink>
          <NavLink className="dashboardNavlinkList" to="/mitarbeiter">
            {workerCountContentBox}
          </NavLink>
          <NavLink
            className="dashboardNavlinkList"
            to="/document?view=new-files"
          >
            {newFilesCountContentBox}
          </NavLink>
        </List>
        <div className="dashboardStatisticSeparator" aria-hidden />
        <NavLink
          className="dashboardNavlink dashboardStatisticProgressLink"
          to="/mitarbeiter"
        >
          {registeredWorkerCountContentBox}
        </NavLink>
      </>
    );
  }

  let statisticBox = <></>;
  if (subdomain === "manage") {
    statisticBox = (
      <RoundedContentBox
        className="fullHeight dashboardRowCard dashboardClickableSurface dashboardStatisticsCard"
        style={{ gap: 0 }}
      >
        {statisticContentBox}
      </RoundedContentBox>
    );
  }
  if (subdomain === "manage" || subdomain === "portal") {
    contactContentBox = (
      <section className="contactSection">
        <div className="contactActionRow">
          <a
            className="contactActionCard dashboardClickableSurface"
            href="mailto:service@kaerhealth.com"
            draggable={false}
            onClick={(e) => {
              if ((window.getSelection()?.toString().length ?? 0) > 0) {
                e.preventDefault();
              }
            }}
          >
            <span
              className="contactActionCardIcon dashboardStatIcon"
              aria-hidden
            >
              <KaerIcon
                width="2.75rem"
                height="2.75rem"
                color="#FF6428"
                icon="mail"
              />
            </span>
            <div className="contactActionCardBody">
              <h3 className="contactActionCardTitle">{t("email")}</h3>
              <p
                className="noMargin contactActionCardText contactActionCardTextSelectable"
                onClick={(e) => e.preventDefault()}
                onMouseDown={(e) => e.stopPropagation()}
                onDragStart={(e) => e.preventDefault()}
                draggable={false}
              >
                {t("write_us_an_email_at")} {t("email_service_kaer")}
              </p>
            </div>
          </a>
          <a
            className="contactActionCard dashboardClickableSurface"
            href="tel:+498941433333"
            draggable={false}
            onClick={(e) => {
              if ((window.getSelection()?.toString().length ?? 0) > 0) {
                e.preventDefault();
              }
            }}
          >
            <span
              className="contactActionCardIcon dashboardStatIcon"
              aria-hidden
            >
              <LocalPhoneOutlinedIcon
                className="contactActionCardPhoneIcon"
                aria-hidden
              />
            </span>
            <div className="contactActionCardBody">
              <h3 className="contactActionCardTitle">{t("phone")}</h3>
              <p
                className="noMargin contactActionCardText contactActionCardTextSelectable"
                onClick={(e) => e.preventDefault()}
                onMouseDown={(e) => e.stopPropagation()}
                onDragStart={(e) => e.preventDefault()}
                draggable={false}
              >
                {t("dashboard_call_card_text")}
              </p>
            </div>
          </a>
        </div>
        <p className="noMargin contactOfficeHours">
          {t("our_office_hours_text")}
        </p>
      </section>
    );
  }
  let contactBox = <></>;
  if (subdomain === "manage" || subdomain === "portal") {
    contactBox = (
      <RoundedContentBox className="roundedContentBox dashboardContactCard">
        {contactContentBox}
      </RoundedContentBox>
    );
  }
  let enrolmentProgressBox = <></>;
  if (subdomain === "manage" && hasLmsAccess) {
    enrolmentProgressBox = (
      <NavLink className="dashboardNavlink" to={getLmsEnrolmentsRoute(lmsMode)}>
        <RoundedContentBox className="roundedContentBox dashboardClickableSurface">
          {enrolmentProgressContentBox}
        </RoundedContentBox>
      </NavLink>
    );
  }
  // Disabled on live - left in code for future usage
  /*eslint-disable */
  let completedExaminationBox = <></>;
  if (subdomain === "manage" && completedExaminations.length !== 0) {
    completedExaminationBox = (
      <Grid item xs={12} sm={12} md={12}>
        <RoundedContentBox className="roundedContentBox">
          {completedExaminationContentBox}
        </RoundedContentBox>
      </Grid>
    );
  }
  /*eslint-enable */

  let portalUpcomingAppointmentsNavCard = <></>;
  let manageUpcomingAppointmentNavCards = <></>;

  if (subdomain === "portal") {
    portalUpcomingAppointmentsNavCard = (
      <NavLink
        className="dashboardNavlink"
        to={`/appointmentrequestopen#${BOOKED_APPOINTMENTS_SECTION_ID}`}
      >
        <RoundedContentBox className="dashboardRowCard dashboardClickableSurface">
          {upcomingApointmentRequestsContentBox}
        </RoundedContentBox>
      </NavLink>
    );
  }

  if (subdomain === "manage") {
    manageUpcomingAppointmentNavCards = (
      <div className="dashboardUpcomingFullWidthStack">
        <NavLink
          className="dashboardNavlink"
          to={`/appointmentrequestopen#${BOOKED_APPOINTMENTS_SECTION_ID}`}
        >
          <RoundedContentBox className="dashboardRowCard dashboardClickableSurface">
            {manageBookedAppointmentsCardInner}
          </RoundedContentBox>
        </NavLink>
        <RoundedContentBox className="dashboardRowCard">
          {manageDepartmentRequestsCardInner}
        </RoundedContentBox>
      </div>
    );
  }

  const dashboardRightColumn = (
    <div className="dashboardCardGrid__cell dashboardCardStack">
      <Stack
        spacing={2}
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
        }}
      >
        <Box className="dashboardCardStack__contactGrow">{contactBox}</Box>
        {enrolmentProgressBox}
      </Stack>
    </div>
  );

  const PortalHeading: FC = () => {
    const getGreeting = () => {
      const currentHour = new Date().getHours();
      if (currentHour < 12) {
        return t("good_morning");
      } else if (currentHour < 18) {
        return t("good_afternoon");
      } else {
        return t("good_evening");
      }
    };

    const greeting = getGreeting();
    let heading;
    switch (subdomain) {
      case "admin":
        heading =
          greeting +
          " " +
          firstName +
          ", " +
          t("kaer_welcome", { portalName: t("staff") });
        break;
      case "manage":
        heading =
          greeting +
          " " +
          firstName +
          ", " +
          t("kaer_welcome", { portalName: t("manager") });
        break;
      default:
        heading =
          greeting +
          " " +
          firstName +
          ", " +
          t("kaer_welcome", { portalName: t("customer") });
        break;
    }

    return <p className="noMargin">{heading}</p>;
  };
  return (
    <>
      <Grid container marginBottom={6} rowSpacing={"1rem"}>
        <Grid item xs={12} mt={"1rem"}>
          <RoundedContentBox className="portalHeading">
            <PortalHeading />
          </RoundedContentBox>
        </Grid>
        <Grid container item columnSpacing={"1rem"} rowSpacing={"1rem"}>
          <Grid
            item
            xs={12}
            sm={12}
            md={topStatColumnMd}
            sx={{ display: "flex" }}
          >
            <NavLink className="dashboardNavlink" to="/todo">
              <RoundedContentBox className="dashboardTopStatCard dashboardClickableSurface">
                {todoCounterContentBox}
              </RoundedContentBox>
            </NavLink>
          </Grid>
          {showTuitionTodoCard && (
            <Grid
              item
              xs={12}
              sm={12}
              md={topStatColumnMd}
              sx={{ display: "flex" }}
            >
              <NavLink
                className="dashboardNavlink"
                to={getLmsCoursesRoute(lmsMode)}
              >
                <RoundedContentBox className="dashboardTopStatCard dashboardClickableSurface">
                  {openEnrolmentCounterContentBox}
                </RoundedContentBox>
              </NavLink>
            </Grid>
          )}
          {subdomain !== "admin" && (
            <Grid
              item
              xs={12}
              sm={12}
              md={topStatColumnMd}
              sx={{ display: "flex" }}
            >
              {appointmentRequestsBox}
            </Grid>
          )}
        </Grid>

        {(subdomain === "manage" || subdomain === "portal") && (
          <Grid item xs={12}>
            {subdomain === "manage" ? (
              <div className="dashboardManageBelowStats">
                <div className="dashboardCardGrid">
                  <div className="dashboardCardGrid__cell">{statisticBox}</div>
                  {dashboardRightColumn}
                </div>
                <div className="dashboardUpcomingFullWidth">
                  {manageUpcomingAppointmentNavCards}
                </div>
              </div>
            ) : (
              <div className="dashboardCardGrid">
                <div className="dashboardCardGrid__cell">
                  {portalUpcomingAppointmentsNavCard}
                </div>
                {dashboardRightColumn}
              </div>
            )}
          </Grid>
        )}
        {subdomain === "admin" && loginPermissions.includes("frontend_view_company") && loginPermissions.includes("view_company") && (
          <DraftCompaniesWidget />
        )}
        {introVideoUrl && (
          <Grid item xs={12}>
            <IntroVideoCard url={introVideoUrl} />
          </Grid>
        )}
      </Grid>
      {errorModal}
    </>
  );
}
